import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchProjectDetail, projectPath, type ProjectDetailResponse } from "../lib/projects";
import { formatCurrency } from "../lib/utils";

function formatMaterialCost(value: number) {
  return value > 0 ? formatCurrency(value) : "Variable";
}

export function ProjectPage() {
  const { slug, projectId } = useParams();
  const [data, setData] = useState<ProjectDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (projectId) {
          params.set("id", projectId);
        } else if (slug) {
          params.set("slug", decodeURIComponent(slug));
        }

        const result = await fetchProjectDetail(params);
        if (!ignore) {
          setData(result);
        }
      } catch {
        if (!ignore) {
          setError("Unable to load this project right now.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, [projectId, slug]);

  const project = data?.project;
  const projectBody = project?.description || project?.summary || "No project description available yet.";

  return (
    <section className="section-shell py-12">
      {loading ? (
        <div className="glass-panel rounded-[32px] p-10 text-sm text-nv-slate">Loading project...</div>
      ) : error || !project ? (
        <div className="glass-panel rounded-[32px] p-10">
          <h1 className="text-3xl font-semibold tracking-tight">Project unavailable</h1>
          <p className="mt-3 text-sm text-nv-slate">{error || "This project could not be found."}</p>
          <Link className="brand-button mt-6 inline-flex rounded-full px-5 py-3 text-sm font-semibold text-white" to="/projects">
            Back to projects
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="flex flex-wrap items-center gap-2 text-sm text-nv-slate">
            <Link className="hover:text-nv-electric" to="/">
              Home
            </Link>
            <span>/</span>
            <Link className="hover:text-nv-electric" to="/projects">
              Projects
            </Link>
            <span>/</span>
            <span className="text-nv-ink">{project.title}</span>
          </div>

          <div className="grid gap-8 xl:grid-cols-[minmax(0,1.2fr)_420px]">
            <div className="space-y-6">
              <div className="glass-panel overflow-hidden rounded-[36px]">
                <div className="aspect-[16/10] bg-black/[0.04]">
                  {project.image ? (
                    <img alt={project.title} className="h-full w-full object-cover" src={project.image} />
                  ) : (
                    <div className="grid h-full place-items-center text-sm text-nv-slate">Project visual coming soon</div>
                  )}
                </div>
              </div>

              <div className="glass-panel rounded-[32px] p-8">
                <p className="eyebrow">Project summary</p>
                <h1 className="mt-3 text-4xl font-semibold tracking-tight text-nv-ink">{project.title}</h1>
                <div
                  className="project-rich-content mt-5 text-base leading-8 text-nv-slate"
                  dangerouslySetInnerHTML={{ __html: projectBody }}
                />
              </div>
            </div>

            <aside className="space-y-6 xl:sticky xl:top-24 xl:self-start">
              <div className="glass-panel rounded-[32px] p-8">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-nv-slate">Project economics</p>
                <div className="mt-5 grid gap-4">
                  <div className="rounded-[24px] border border-black/10 bg-black/[0.03] p-5 dark:border-white/8 dark:bg-white/[0.03]">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-nv-slate">Service charge</p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight text-nv-ink">{formatCurrency(project.cost)}</p>
                  </div>
                  <div className="rounded-[24px] border border-black/10 bg-black/[0.03] p-5 dark:border-white/8 dark:bg-white/[0.03]">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-nv-slate">Material cost</p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight text-nv-ink">{formatMaterialCost(project.materialCost)}</p>
                  </div>
                </div>

                <Link className="brand-button mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold text-white" to="/projects">
                  Explore more projects
                </Link>
              </div>

              {data.related.length > 0 ? (
                <div className="glass-panel rounded-[32px] p-6">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Related work</p>
                      <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">More projects</h2>
                    </div>
                    <Link className="text-sm font-semibold text-nv-electric" to="/projects">
                      View all
                    </Link>
                  </div>
                  <div className="mt-5 space-y-3">
                    {data.related.map((item) => (
                      <Link className="block rounded-[24px] border border-black/10 bg-black/[0.03] p-4 transition hover:bg-black/[0.05] dark:border-white/8 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]" key={item.id} to={projectPath(item.slug, item.id)}>
                        <p className="font-semibold text-nv-ink">{item.title}</p>
                        <p className="mt-2 line-clamp-3 text-sm leading-6 text-nv-slate">{item.summary}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}
            </aside>
          </div>
        </div>
      )}
    </section>
  );
}
